import React, { useEffect, useState } from "react";

import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { FormSelectField } from "@components-shared/desktop/inputs";
import { Button } from "@components/buttons";
import {
	OnboardingRegistrationDOB,
	OnboardingRegistrationEmail,
	OnboardingRegistrationFirstName,
	OnboardingRegistrationLastName,
	OnboardingRegistrationPassword,
	OnboardingRegistrationTitle,
	OnboardingRegistrationUsername,
} from "@features/membership/desktop/registration-types/onboarding-registration";
import RegistrationInputErrorWrapper from "@features/membership/shared/registration-types/shared/RegistrationInputErrorWrapper";
import { useRootAppStore } from "@hooks";
import { useRegistrationStore } from "@state/context/membership/registration-types/RegistrationStoreContext";
import { useAppContext } from "@state/hooks/shared";
import { getCurrentCulture, validateCountry } from "@utils";
import { isRegistrationDomain } from "@utils/membership/membershipUtils";
import { observer } from "mobx-react";

export default observer(function OnboardingRegistrationAccountDetails() {
	const { t } = useTranslation(["registration", "password-change"]);
	const { trigger, setValue, getValues } = useFormContext();

	const { isIpRestricted } = useRootAppStore();

	const [isNextButtonDisabled, setIsNextButtonDisabled] =
		useState<boolean>(false);

	const { culture } = useAppContext();

	const {
		titleData,
		countryData,
		checkUserNameAvailability,
		checkIsEmailAvailable,
		checkPasswordSimilarity,
		setCurrentStep,
		toggleStepSwitchFlag,
		isSwitchingSteps,
	} = useRegistrationStore();

	const germany = countryData?.find((country) => country.abrv === "germany");

	useEffect(() => {
		if (germany != null) {
			setValue("countryId", germany.id);
		}
	}, [countryData]);

	useEffect(() => {
		document.querySelector("html, body")?.scrollTo(0, 0);
	}, []);

	return (
		<>
			<div className="card--membership__form">
				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_title"}
				>
					<OnboardingRegistrationTitle titleData={titleData} />
				</RegistrationInputErrorWrapper>
				<div className="form__field form--grid u-mar--bottom--reset">
					<div className="form--grid__row row">
						<div className="form--grid__col col col-sml-6">
							<RegistrationInputErrorWrapper
								componentName={
									"onboarding-registration_first-name"
								}
							>
								<OnboardingRegistrationFirstName labelClassName="label--strong" />
							</RegistrationInputErrorWrapper>
						</div>
						<div className="form--grid__col col col-sml-6">
							<RegistrationInputErrorWrapper
								componentName={
									"onboarding-registration_last-name"
								}
							>
								<OnboardingRegistrationLastName labelClassName="label--strong" />
							</RegistrationInputErrorWrapper>
						</div>
					</div>
				</div>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_dob"}
				>
					<OnboardingRegistrationDOB
						countryData={countryData}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_country"}
				>
					<FormSelectField
						name="countryId"
						className="form__field u-mar--bottom--reset"
						inputClassName="input input--sml input--select"
						labelClassName="label--strong"
						label={t("COUNTRY_LABEL")}
						placeholder={t("COUNTRY_PLACEHOLDER")}
						dropdownData={germany != null ? [germany] : []}
						validate={{
							isCountryValidDependantOnDoB: (value: string) =>
								validateCountry(
									getValues("dob"),
									countryData,
									value
								),
						}}
						value={germany != null ? germany.id : null}
						required
						disabled
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_username"}
				>
					<OnboardingRegistrationUsername
						checkUserNameAvailability={checkUserNameAvailability}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_password"}
				>
					<OnboardingRegistrationPassword
						checkPasswordSimilarity={checkPasswordSimilarity}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>

				<RegistrationInputErrorWrapper
					componentName={"onboarding-registration_email"}
				>
					<OnboardingRegistrationEmail
						checkIsEmailAvailable={checkIsEmailAvailable}
						labelClassName="label--strong"
					/>
				</RegistrationInputErrorWrapper>
			</div>

			<Button
				className="btn--base btn--block btn--accent"
				iconRight={isSwitchingSteps ? false : true}
				iconClassName={
					isSwitchingSteps
						? ""
						: "u-icon--tny u-icon--arrow-right u-color--accent--text"
				}
				btnText={t("COMMON.NEXT_BUTTON")}
				type="button"
				isLoading={isSwitchingSteps}
				disabled={isIpRestricted || isNextButtonDisabled}
				onClick={async () => {
					toggleStepSwitchFlag(true);
					setIsNextButtonDisabled(true);
					const isValid = await trigger();
					setIsNextButtonDisabled(false);

					if (isValid) {
						setCurrentStep(2, true);
					}
					toggleStepSwitchFlag(false);
				}}
			/>

			<div className="card--membership__footer">
				{t("EXISTING_ACCOUNT")}
				{isRegistrationDomain() ? (
					<a
						href={`${WEBPACK_WEBSITE_URL}/${culture}/auth/login`}
						className="u-anchor u-anchor--primary u-anchor--underline"
					>
						&nbsp;{t("COMMON.LOGIN_BUTTON")}
					</a>
				) : (
					<Link
						to={`/${getCurrentCulture()}/auth/login`}
						className="u-anchor u-anchor--primary u-anchor--underline"
					>
						&nbsp;{t("COMMON.LOGIN_BUTTON")}
					</Link>
				)}
			</div>
		</>
	);
});
